import { eq, and, isNull, asc, sql } from 'drizzle-orm';
import { db } from '@/db/client';
import { creditCards, accounts, invoices, transactions, recurringTransactions } from '@/db/schema';
import { AdjustmentType } from '@/lib/constants';
import { getGuaranteedLimitByCard } from '@/db/repositories/reservaRepository';
import { generateId } from '@/lib/id';

export type CreditCard = typeof creditCards.$inferSelect;
export type NewCreditCard = Omit<typeof creditCards.$inferInsert, 'id' | 'createdAt' | 'updatedAt'>;

export type CreditCardWithAccount = CreditCard & {
    paymentAccountName?: string | null;
    paymentAccountColor?: string | null;
    paymentAccountIcon?: string | null;
};

export async function getCreditCards(includeArchived = false): Promise<CreditCardWithAccount[]> {
    const conditions = [isNull(creditCards.deletedAt)];
    if (!includeArchived) {
        conditions.push(eq(creditCards.isArchived, 0));
    }

    const rows = await db
        .select({
            id: creditCards.id,
            name: creditCards.name,
            brand: creditCards.brand,
            institution: creditCards.institution,
            institutionIcon: creditCards.institutionIcon,
            lastFourDigits: creditCards.lastFourDigits,
            creditLimit: creditCards.creditLimit,
            closingDay: creditCards.closingDay,
            dueDay: creditCards.dueDay,
            dueOnBusinessDay: creditCards.dueOnBusinessDay,
            closingDayToNextInvoice: creditCards.closingDayToNextInvoice,
            paymentAccountId: creditCards.paymentAccountId,
            color: creditCards.color,
            icon: creditCards.icon,
            isArchived: creditCards.isArchived,
            sortOrder: creditCards.sortOrder,
            createdAt: creditCards.createdAt,
            updatedAt: creditCards.updatedAt,
            deletedAt: creditCards.deletedAt,
            paymentAccountName: accounts.name,
            paymentAccountColor: accounts.color,
            paymentAccountIcon: accounts.icon,
        })
        .from(creditCards)
        .leftJoin(accounts, eq(creditCards.paymentAccountId, accounts.id))
        .where(and(...conditions))
        .orderBy(asc(creditCards.sortOrder), asc(creditCards.name));

    return rows;
}

export async function getCreditCardById(id: string): Promise<CreditCardWithAccount | null> {
    const rows = await db
        .select({
            id: creditCards.id,
            name: creditCards.name,
            brand: creditCards.brand,
            institution: creditCards.institution,
            institutionIcon: creditCards.institutionIcon,
            lastFourDigits: creditCards.lastFourDigits,
            creditLimit: creditCards.creditLimit,
            closingDay: creditCards.closingDay,
            dueDay: creditCards.dueDay,
            dueOnBusinessDay: creditCards.dueOnBusinessDay,
            closingDayToNextInvoice: creditCards.closingDayToNextInvoice,
            paymentAccountId: creditCards.paymentAccountId,
            color: creditCards.color,
            icon: creditCards.icon,
            isArchived: creditCards.isArchived,
            sortOrder: creditCards.sortOrder,
            createdAt: creditCards.createdAt,
            updatedAt: creditCards.updatedAt,
            deletedAt: creditCards.deletedAt,
            paymentAccountName: accounts.name,
            paymentAccountColor: accounts.color,
            paymentAccountIcon: accounts.icon,
        })
        .from(creditCards)
        .leftJoin(accounts, eq(creditCards.paymentAccountId, accounts.id))
        .where(and(eq(creditCards.id, id), isNull(creditCards.deletedAt)));

    return rows[0] ?? null;
}

export async function createCreditCard(data: NewCreditCard): Promise<CreditCard> {
    const id = generateId();
    const now = new Date().toISOString();
    await db.insert(creditCards).values({
        ...data,
        id,
        createdAt: now,
        updatedAt: now,
    });
    const result = await db.select().from(creditCards).where(eq(creditCards.id, id));
    return result[0];
}

export async function updateCreditCard(id: string, data: Partial<NewCreditCard>): Promise<void> {
    await db
        .update(creditCards)
        .set({ ...data, updatedAt: new Date().toISOString() })
        .where(eq(creditCards.id, id));
}

export async function archiveCreditCard(id: string): Promise<void> {
    await db
        .update(creditCards)
        .set({ isArchived: 1, updatedAt: new Date().toISOString() })
        .where(eq(creditCards.id, id));
}

export async function unarchiveCreditCard(id: string): Promise<void> {
    await db
        .update(creditCards)
        .set({ isArchived: 0, updatedAt: new Date().toISOString() })
        .where(eq(creditCards.id, id));
}

/**
 * Excluir cartão exclui de verdade (ADR 0068): leva junto tudo que é do LADO DO CARTÃO e
 * ainda pendente — a despesa prevista "Fatura X" e o pagamento de fatura não confirmado
 * (senão viram fantasma congelado no fluxo projetado: nada mais lê a fatura de um cartão
 * excluído pra atualizar/curar o retrato) e as recorrências do cartão (senão seguem
 * materializando ocorrência nova em fatura morta). O que tocou saldo de conta (pagamento
 * confirmado) FICA; a compra crua confirmada também — some das leituras pelo filtro de
 * cartão excluído. Por isso o cartão excluído tem que viajar no backup enquanto houver
 * lançamento apontando pra ele (ADR 0165): sem a linha, o restore deixava a compra órfã,
 * o filtro deixava de escondê-la e a fatura dava "Credit card not found" (REACT-NATIVE-2V).
 */
export async function deleteCreditCard(id: string): Promise<void> {
    const now = new Date().toISOString();

    await db
        .update(creditCards)
        .set({ deletedAt: now })
        .where(eq(creditCards.id, id));

    await db
        .update(transactions)
        .set({ deletedAt: now, updatedAt: now })
        .where(
            and(
                eq(transactions.creditCardId, id),
                eq(transactions.isConfirmed, 0),
                isNull(transactions.deletedAt),
                sql`${transactions.adjustmentType} IN (${AdjustmentType.INVOICE_EXPENSE}, ${AdjustmentType.INVOICE_PAYMENT})`,
            ),
        );

    await db
        .update(recurringTransactions)
        .set({ deletedAt: now, isActive: 0, updatedAt: now })
        .where(
            and(
                eq(recurringTransactions.creditCardId, id),
                isNull(recurringTransactions.deletedAt),
            ),
        );
}

/**
 * Grava a ordem manual dos cartões: a posição na lista vira o `sortOrder`.
 * Recebe só os cartões visíveis (ativos) — arquivado não entra na tela de reordenação
 * e mantém o `sortOrder` que tinha, reassumindo o lugar se for reativado.
 */
export async function reorderCreditCards(orderedIds: string[]): Promise<void> {
    const now = new Date().toISOString();
    for (let i = 0; i < orderedIds.length; i++) {
        await db
            .update(creditCards)
            .set({ sortOrder: i, updatedAt: now })
            .where(eq(creditCards.id, orderedIds[i]));
    }
}

export async function getUsedLimit(cardId: string): Promise<number> {
    // Outstanding amount across invoices still consumes the card limit.
    const result = await db
        .select({
            total: sql<number>`COALESCE(SUM(CASE WHEN ${invoices.totalAmount} > ${invoices.paidAmount} THEN ${invoices.totalAmount} - ${invoices.paidAmount} ELSE 0 END), 0)`.as('total'),
        })
        .from(invoices)
        .where(
            and(
                eq(invoices.creditCardId, cardId),
                sql`${invoices.totalAmount} > ${invoices.paidAmount}`,
            ),
        );

    return result[0]?.total ?? 0;
}

export async function getAvailableLimit(cardId: string): Promise<number> {
    const card = await db.select().from(creditCards).where(eq(creditCards.id, cardId));
    if (!card[0]) return 0;

    const used = await getUsedLimit(cardId);
    // Limite garantido: caixinhas vinculadas ao cartão somam ao disponível.
    const guaranteed = await getGuaranteedLimitByCard(cardId);
    return Math.max(0, card[0].creditLimit + guaranteed - used);
}
